import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Mail } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../ui/dialog";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "../ui/table";
import { toast } from "sonner";
import api, { eur, formatApiErrorDetail } from "../../lib/api";

const emptyAgent = { name: "", email: "", password: "", commission_rate: 8, discount_pct: 0, phone: "", zone: "" };

export default function AgentsPanel() {
  const [agents, setAgents] = useState([]);
  const [agentForm, setAgentForm] = useState(emptyAgent);
  const [agentEditId, setAgentEditId] = useState(null);
  const [agentOpen, setAgentOpen] = useState(false);

  const load = () => api.get("/agents").then(({ data }) => setAgents(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAgent = (a) => { setAgentForm(a ? { ...a, password: "" } : emptyAgent); setAgentEditId(a?.id || null); setAgentOpen(true); };
  const saveAgent = async () => {
    try {
      if (agentEditId) {
        const payload = { name: agentForm.name, commission_rate: Number(agentForm.commission_rate), discount_pct: Number(agentForm.discount_pct) || 0, phone: agentForm.phone, zone: agentForm.zone };
        if (agentForm.password) payload.password = agentForm.password;
        await api.put(`/agents/${agentEditId}`, payload);
      } else {
        await api.post("/agents", { ...agentForm, commission_rate: Number(agentForm.commission_rate), discount_pct: Number(agentForm.discount_pct) || 0 });
      }
      toast.success("Agente salvato"); setAgentOpen(false); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const delAgent = async (id) => { await api.delete(`/agents/${id}`); toast.success("Eliminato"); load(); };
  const sendCommissions = async (id) => {
    try { const { data } = await api.post(`/agents/${id}/send-commissions`); toast.success(`Email inviata · ${eur(data.total_commissions)} di provvigioni`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Dialog open={agentOpen} onOpenChange={setAgentOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => openAgent(null)} data-testid="add-agent-btn" className="bg-[#0F4C81] text-white rounded-full"><Plus className="w-4 h-4 mr-2" />Nuovo agente</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle className="font-serif text-2xl">{agentEditId ? "Modifica" : "Nuovo"} agente</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Nome</Label><Input data-testid="agent-name-input" value={agentForm.name} onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input type="email" disabled={!!agentEditId} data-testid="agent-email-input" value={agentForm.email} onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })} className="mt-1" /></div>
              <div><Label>Password {agentEditId && <span className="text-xs text-stone-400">(lascia vuoto per non modificare)</span>}</Label><Input type="password" data-testid="agent-password-input" value={agentForm.password} onChange={(e) => setAgentForm({ ...agentForm, password: e.target.value })} className="mt-1" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Provvigione %</Label><Input type="number" step="0.5" data-testid="agent-commission-input" value={agentForm.commission_rate} onChange={(e) => setAgentForm({ ...agentForm, commission_rate: e.target.value })} className="mt-1" /></div>
                <div><Label>Sconto listino %</Label><Input type="number" step="0.5" data-testid="agent-discount-input" value={agentForm.discount_pct} onChange={(e) => setAgentForm({ ...agentForm, discount_pct: e.target.value })} className="mt-1" /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Zona</Label><Input value={agentForm.zone} onChange={(e) => setAgentForm({ ...agentForm, zone: e.target.value })} className="mt-1" /></div>
                <div><Label>Telefono</Label><Input value={agentForm.phone} onChange={(e) => setAgentForm({ ...agentForm, phone: e.target.value })} className="mt-1" /></div>
              </div>
            </div>
            <DialogFooter><Button onClick={saveAgent} data-testid="save-agent-btn" className="bg-[#0F4C81] text-white rounded-full">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 overflow-x-auto">
        <Table>
          <TableHeader><TableRow><TableHead>Nome</TableHead><TableHead>Email</TableHead><TableHead>Provvigione</TableHead><TableHead>Sconto</TableHead><TableHead>Zona</TableHead><TableHead>Azioni</TableHead></TableRow></TableHeader>
          <TableBody>
            {agents.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-stone-400 py-8">Nessun agente</TableCell></TableRow>}
            {agents.map((a) => (
              <TableRow key={a.id} data-testid={`admin-agent-${a.id}`}>
                <TableCell className="font-medium">{a.name}</TableCell>
                <TableCell className="text-stone-500">{a.email}</TableCell>
                <TableCell className="font-mono">{a.commission_rate}%</TableCell>
                <TableCell className="font-mono">{a.discount_pct ?? 0}%</TableCell>
                <TableCell>{a.zone || "—"}</TableCell>
                <TableCell>
                  <div className="flex gap-2">
                    <button onClick={() => sendCommissions(a.id)} data-testid={`send-commissions-${a.id}`} title="Invia liquidazione via email"><Mail className="w-4 h-4 text-stone-500 hover:text-[#15803D]" /></button>
                    <button onClick={() => openAgent(a)} data-testid={`edit-agent-${a.id}`}><Pencil className="w-4 h-4 text-stone-500 hover:text-[#0F4C81]" /></button>
                    <button onClick={() => delAgent(a.id)} data-testid={`delete-agent-${a.id}`}><Trash2 className="w-4 h-4 text-red-500" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
